import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiMail, FiShield, FiArrowRight, FiZap } from 'react-icons/fi';
import ProviderCard from './components/ProviderCard';
import SavedSmtpList from './components/SavedSmtpList';
import SmtpConfigForm from './components/SmtpConfigForm';
import EmailService from '../../services/EmailService';
import SmtpConfigService from '../../services/SmtpConfigService';
import { getGmailOAuthUrl, GOOGLE_REDIRECT_ORIGIN } from './GmailAuthHelper';
import { getZohoOAuthUrl, ZOHO_REDIRECT_ORIGIN } from './ZohoAuthHelper';

const buildProviders = (smtpCount) => [
  {
    id: 'gmail',
    name: 'Gmail',
    subtitle: 'Google Workspace · Gmail',
    description: 'Sign in with your Google account to send and receive email directly inside WebinarCRM.',
    badge: { type: 'popular', label: 'Most Popular' },
    logo: {
      bg: '#fff',
      node: (
        <svg width="36" height="36" viewBox="0 0 48 48">
          <path fill="#4CAF50" d="M45,16.2l-5,2.75l-5,4.75L35,40h7c1.657,0,3-1.343,3-3V16.2z" />
          <path fill="#1976D2" d="M3,16.2l3.819,1.807L13,23.7V40H6c-1.657,0-3-1.343-3-3V16.2z" />
          <polygon fill="#E53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17" />
          <path fill="#C62828" d="M3,12.298V16.2l10,7.5V11.2L9.876,8.859C9.132,8.301,8.228,8,7.298,8h0 C4.924,8,3,9.924,3,12.298z" />
          <path fill="#FFC107" d="M45,12.298V16.2l-10,7.5V11.2l3.124-2.341C38.868,8.301,39.772,8,40.702,8h0 C43.076,8,45,9.924,45,12.298z" />
        </svg>
      ),
    },
    infoText: 'Clicking "Connect" will open Google\'s secure authorization page in a new tab. We only request permission to send emails on your behalf — we never read your inbox.',
    ctaLabel: 'Connect Gmail',
  },
  {
    id: 'zoho',
    name: 'Zoho Mail',
    subtitle: 'Zoho Mail · Zoho Workplace',
    description: 'Connect your Zoho Mail account. Ideal if your team is already on the Zoho ecosystem.',
    badge: { type: 'recommended', label: 'Recommended' },
    logo: {
      bg: '#e42527',
      node: <span style={{ color: '#fff', fontWeight: 800, fontSize: 22, fontFamily: 'Inter, sans-serif' }}>Z</span>,
    },
    infoText: "Clicking \"Connect\" will open Zoho's secure authorization page. We only request permission to send emails on your behalf.",
    ctaLabel: 'Connect Zoho Mail',
  },
  {
    id: 'smtp',
    name: 'Other SMTP',
    subtitle: 'Custom SMTP · Any Provider',
    description: 'Connect Outlook, Yahoo, Postmark, SendGrid and more using your SMTP credentials.',
    badge: smtpCount > 0 ? { type: 'saved', label: `${smtpCount} saved` } : null,
    logo: {
      bg: '#1e293b',
      node: <FiMail size={22} color="#fff" />,
    },
    infoText: 'Enter your SMTP server credentials to connect. Your password is encrypted and never stored in plain text.',
    ctaLabel: 'Continue with SMTP',
  },
];

const ConnectEmailPage = ({ onConnect, onCancel }) => {
  const [selected, setSelected] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [showSmtpForm, setShowSmtpForm] = useState(false);

  useEffect(() => {
    setSmtpConfigs(SmtpConfigService.getConfigs());
  }, []);

  const providers = buildProviders(smtpConfigs.length);

  // Popup postMessage delivery isn't guaranteed — this ref dedupes so the
  // message path and the popup-closed fallback below never both fire.
  // Mirrors the Gmail block below.
  const zohoResolvedRef = useRef(false);
  const zohoPollRef = useRef(null);

  const completeZohoConnect = useCallback(() => {
    if (zohoResolvedRef.current) return;
    zohoResolvedRef.current = true;
    // Zoho's connect response carries no email (only a connection status),
    // so — same as before this migration — the account entry starts blank.
    EmailService.storeZohoAuth('');
    onConnect({ type: 'zoho', email: '', label: 'Zoho Mail' });
    setConnecting(false);
  }, [onConnect]);

  useEffect(() => {
    const handleZohoOAuthMessage = (event) => {
      if (event.origin !== ZOHO_REDIRECT_ORIGIN) return;

      if (event.data?.type === 'ZOHO_OAUTH_SUCCESS') {
        completeZohoConnect();
      }

      if (event.data?.type === 'ZOHO_OAUTH_ERROR' && !zohoResolvedRef.current) {
        zohoResolvedRef.current = true;
        setError(event.data.message || 'Failed to integrate Zoho Mail.');
        setConnecting(false);
      }
    };

    window.addEventListener('message', handleZohoOAuthMessage);
    return () => window.removeEventListener('message', handleZohoOAuthMessage);
  }, [completeZohoConnect]);

  useEffect(() => () => { if (zohoPollRef.current) clearInterval(zohoPollRef.current); }, []);

  const zohoLogin = () => {
    try {
      zohoResolvedRef.current = false;
      const popup = window.open(getZohoOAuthUrl(), 'Zoho OAuth', 'width=520,height=700');
      if (!popup) {
        setError('Please allow popups to integrate Zoho Mail.');
        setConnecting(false);
        return;
      }

      // Fallback for when postMessage never arrives: once the popup closes, ask
      // the backend directly whether the connection actually went through.
      zohoPollRef.current = setInterval(async () => {
        if (!popup.closed) return;
        clearInterval(zohoPollRef.current);
        zohoPollRef.current = null;
        if (zohoResolvedRef.current) return;

        const status = await EmailService.getZohoConnectionStatus().catch(() => null);
        if (status?.connected) {
          completeZohoConnect();
        } else {
          zohoResolvedRef.current = true;
          setConnecting(false);
        }
      }, 500);
    } catch (e) {
      setError(e.message || 'Failed to start Zoho Mail integration.');
      setConnecting(false);
    }
  };

  // Popup postMessage delivery isn't guaranteed — some browsers (COOP behavior,
  // or the window.close() race in GoogleCallback.js) silently drop it. This ref
  // dedupes so the message path and the popup-closed fallback below never both fire.
  const gmailResolvedRef = useRef(false);
  const gmailPollRef = useRef(null);

  const completeGmailConnect = useCallback(async () => {
    if (gmailResolvedRef.current) return;
    gmailResolvedRef.current = true;
    try {
      const userInfo = await EmailService.getGoogleUserInfo();
      const email = userInfo.data?.email || '';
      EmailService.storeGmailAuth(email);
      onConnect({ type: 'gmail', email, label: 'Gmail' });
    } catch {
      EmailService.storeGmailAuth('');
      onConnect({ type: 'gmail', email: '', label: 'Gmail' });
    } finally {
      setConnecting(false);
    }
  }, [onConnect]);

  useEffect(() => {
    const handleGoogleOAuthMessage = (event) => {
      if (event.origin !== GOOGLE_REDIRECT_ORIGIN) return;

      if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
        completeGmailConnect();
      }

      if (event.data?.type === 'GOOGLE_OAUTH_ERROR' && !gmailResolvedRef.current) {
        gmailResolvedRef.current = true;
        setError('Google sign-in failed. Please try again.');
        setConnecting(false);
      }
    };

    window.addEventListener('message', handleGoogleOAuthMessage);
    return () => window.removeEventListener('message', handleGoogleOAuthMessage);
  }, [completeGmailConnect]);

  useEffect(() => () => { if (gmailPollRef.current) clearInterval(gmailPollRef.current); }, []);

  const gmailLogin = () => {
    try {
      gmailResolvedRef.current = false;
      const popup = window.open(getGmailOAuthUrl(), 'Gmail OAuth', 'width=520,height=700');
      if (!popup) {
        setError('Please allow popups to integrate Gmail.');
        setConnecting(false);
        return;
      }

      // Fallback for when postMessage never arrives: once the popup closes, ask
      // the backend directly whether the connection actually went through — the
      // same source of truth a manual page refresh already relies on.
      gmailPollRef.current = setInterval(async () => {
        if (!popup.closed) return;
        clearInterval(gmailPollRef.current);
        gmailPollRef.current = null;
        if (gmailResolvedRef.current) return;

        const status = await EmailService.getGmailConnectionStatus().catch(() => null);
        if (status?.connected) {
          completeGmailConnect();
        } else {
          gmailResolvedRef.current = true;
          setConnecting(false);
        }
      }, 500);
    } catch (e) {
      setError(e.message || 'Failed to start Gmail integration.');
      setConnecting(false);
    }
  };

  const handleConnect = () => {
    if (!selected) return;
    const provider = providers.find((p) => p.id === selected);
    if (provider?.disabled) return;
    setError('');
    if (selected === 'gmail') {
      setConnecting(true);
      gmailLogin();
    } else if (selected === 'zoho') {
      setConnecting(true);
      zohoLogin();
    }
  };

  const handleSmtpSaveAndConnect = (formData) => {
    const saved = SmtpConfigService.saveConfig(formData);
    const updated = SmtpConfigService.getConfigs();
    setSmtpConfigs(updated);
    setShowSmtpForm(false);
    onConnect({
      type: 'smtp',
      config: saved,
      email: saved.senderEmail,
      label: saved.name,
    });
  };

  const handleSmtpConnect = (config) => {
    onConnect({
      type: 'smtp',
      config,
      email: config.senderEmail,
      label: config.name,
    });
  };

  const handleSmtpDelete = (id) => {
    SmtpConfigService.deleteConfig(id);
    setSmtpConfigs(SmtpConfigService.getConfigs());
  };

  const selectedProvider = providers.find((p) => p.id === selected);
  const showSmtpPanel = selected === 'smtp';
  const showSavedList = showSmtpPanel && smtpConfigs.length > 0 && !showSmtpForm;
  const showForm = showSmtpPanel && (smtpConfigs.length === 0 || showSmtpForm);

  return (
    <div className="email-center-v2">
      <div className="ec-connect-page">
        <div className="ec-connect-header">
          <div className="ec-header-icon"><FiMail size={16} /></div>
          Email Center
          {onCancel && (
            <button className="ec-connect-back-btn" onClick={onCancel}>
              ← Back
            </button>
          )}
        </div>

        <div className="ec-connect-body">
          <div className="ec-connect-icon-wrap">
            <FiMail size={26} />
          </div>

          <h2 className="ec-connect-title page-title">Connect Your Email Account</h2>
          <p className="ec-connect-subtitle page-subtitle">
            Choose a provider below to start sending webinar invites,
            reminders, and follow-ups directly from WebinarCRM.
          </p>

          <div className="ec-security-note">
            <FiShield size={14} />
            Credentials are encrypted and stored securely. We never access your email content.
          </div>

          <div className="ec-provider-cards">
            {providers.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                selected={selected === p.id}
                onSelect={(id) => {
                  setSelected(id === selected ? null : id);
                  setShowSmtpForm(false);
                  setError('');
                }}
              />
            ))}
          </div>

          {/* Gmail CTA */}
          {selected && selectedProvider && selected !== 'smtp' && (
            <div className="ec-connect-action-panel">
              <div className="ec-connect-info-box">
                <FiZap className="ec-connect-info-icon" />
                <span>{selectedProvider.infoText}</span>
              </div>
              {error && (
                <div style={{ padding: '10px 18px', color: '#dc2626', fontSize: 13 }}>
                  {error}
                </div>
              )}
              <button
                className="ec-connect-cta"
                onClick={handleConnect}
                disabled={connecting || selectedProvider.disabled}
              >
                {connecting ? (
                  <>
                    <div className="ec-spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} />
                    Connecting...
                  </>
                ) : (
                  <>
                    {selectedProvider.ctaLabel}
                    {!selectedProvider.disabled && <FiArrowRight size={16} />}
                    {selectedProvider.disabled && (
                      <span style={{ fontSize: 12, opacity: 0.8 }}> · Coming Soon</span>
                    )}
                  </>
                )}
              </button>
            </div>
          )}

          {/* SMTP — saved list */}
          {showSavedList && (
            <SavedSmtpList
              configs={smtpConfigs}
              onConnect={handleSmtpConnect}
              onDelete={handleSmtpDelete}
              onAddNew={() => setShowSmtpForm(true)}
            />
          )}

          {/* SMTP — new config form */}
          {showForm && (
            <SmtpConfigForm
              onSaveAndConnect={handleSmtpSaveAndConnect}
              onCancel={() => smtpConfigs.length > 0 ? setShowSmtpForm(false) : setSelected(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectEmailPage;
